import { useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Mail, KeyRound, X, ChevronLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';

/**
 * Two-step forgot-password flow:
 *   1) Email → we always show a generic success (enumeration-safe on backend)
 *   2) Enter 6-digit code + new password → server validates + invalidates
 *      all sessions.
 *
 * Props:
 *   open, onOpenChange, initialEmail
 */
export default function ForgotPasswordDialog({ open, onOpenChange, initialEmail = '' }) {
  const [step, setStep] = useState('request');   // 'request' | 'reset' | 'done'
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const close = () => {
    onOpenChange(false);
    // reset state after the sheet finishes closing
    setTimeout(() => {
      setStep('request');
      setCode('');
      setNewPassword('');
    }, 200);
  };

  const requestCode = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      toast.success('If we know that email, a code is on its way');
      setStep('reset');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) {
      toast.error('Enter the 6-digit code from your email');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/reset-password', {
        email: email.trim(),
        code: code.trim(),
        new_password: newPassword,
      });
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="forgot-password-dialog"
        className="sm:max-w-md p-0 gap-0 bg-white rounded-3xl border border-black/[0.06] overflow-hidden">
        {/* Custom header */}
        <div className="px-6 pt-6 pb-4 flex items-center gap-3 border-b border-black/[0.05]">
          {step === 'reset' && (
            <button
              data-testid="forgot-back"
              onClick={() => setStep('request')}
              className="w-8 h-8 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[#4B4B52]"
              type="button">
              <ChevronLeft size={16} />
            </button>
          )}
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">
              {step === 'done' ? 'ALL SET' : 'ACCOUNT RECOVERY'}
            </p>
            <p className="font-display text-[18px] font-[600] text-[#0F0F10] mt-0.5">
              {step === 'request' && 'Reset your password'}
              {step === 'reset' && 'Enter your code'}
              {step === 'done' && 'Password updated'}
            </p>
          </div>
          <button
            data-testid="forgot-close"
            onClick={close}
            className="w-8 h-8 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[#8A8A93]"
            type="button">
            <X size={14} />
          </button>
        </div>

        {/* Step 1 — request code */}
        {step === 'request' && (
          <form onSubmit={requestCode} className="px-6 py-6 space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-[#B91C1C]/[0.08] flex items-center justify-center">
              <Mail size={22} className="text-[#B91C1C]" strokeWidth={1.8} />
            </div>
            <p className="text-[13px] text-[#4B4B52] leading-relaxed">
              Enter the email you used to sign up. We&apos;ll send you a 6-digit code to reset your password.
            </p>
            <Input
              data-testid="forgot-email"
              type="email"
              autoFocus
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="h-12 rounded-xl bg-[#F8F9FA] border-black/[0.05]"
            />
            <button
              type="submit"
              data-testid="forgot-submit-email"
              disabled={busy}
              className="w-full h-12 rounded-xl bg-[#0F0F10] text-white text-sm font-semibold disabled:opacity-50 active:scale-[0.98] transition">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Send reset code'}
            </button>
          </form>
        )}

        {/* Step 2 — enter code + new password */}
        {step === 'reset' && (
          <form onSubmit={submitReset} className="px-6 py-6 space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-[#B91C1C]/[0.08] flex items-center justify-center">
              <KeyRound size={22} className="text-[#B91C1C]" strokeWidth={1.8} />
            </div>
            <p className="text-[13px] text-[#4B4B52] leading-relaxed">
              Check <span className="font-semibold text-[#0F0F10]">{email}</span> for a 6-digit code. Codes expire after 30 minutes.
            </p>
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">Reset code</label>
              <Input
                data-testid="forgot-code"
                autoFocus
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="h-14 text-center text-2xl font-mono tracking-[10px] rounded-xl bg-[#F8F9FA] border-black/[0.05]"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">New password</label>
              <Input
                data-testid="forgot-new-password"
                type="password"
                placeholder="At least 8 characters"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="h-12 rounded-xl bg-[#F8F9FA] border-black/[0.05]"
              />
            </div>
            <button
              type="submit"
              data-testid="forgot-submit-reset"
              disabled={busy}
              className="w-full h-12 rounded-xl bg-[#B91C1C] text-white text-sm font-semibold disabled:opacity-50 active:scale-[0.98] transition">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Update password'}
            </button>
            <button
              type="button"
              onClick={requestCode}
              className="w-full text-[12px] font-semibold text-[#4B4B52] hover:text-[#0F0F10] py-1">
              Didn&apos;t get a code? Resend
            </button>
          </form>
        )}

        {/* Step 3 — done */}
        {step === 'done' && (
          <div className="px-6 py-8 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-[#DCFCE7] mx-auto flex items-center justify-center">
              <KeyRound size={22} className="text-[#166534]" strokeWidth={2} />
            </div>
            <p className="text-[14px] text-[#0F0F10] font-semibold">Password updated</p>
            <p className="text-[12px] text-[#4B4B52] leading-relaxed">
              You&apos;ve been signed out on all devices for safety. Sign in with your new password.
            </p>
            <button
              onClick={close}
              data-testid="forgot-done-close"
              className="w-full h-12 rounded-xl bg-[#0F0F10] text-white text-sm font-semibold active:scale-[0.98] transition">
              Sign in
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
